import {
    useContext,
    useState,
    Fragment
} from "react";
import {
    View
} from "react-native";
import type IChangeMailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    SERVICE_EVENT_TYPES
} from "../../../../services/restAPI/constants";
import {
    AuthContext
} from "../../../../contexts/AuthContext";
import RESTServiceContext from "../../../../services/restAPI/context";

const ChangeMailModal = ({
    currentMail,
    isVisible,
    onSuccess,
    onClose
}: IChangeMailModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        updateUser
    } = useContext(AuthContext);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        password,
        setPassword
    ] = useState<string>("");

    const [
        newMail,
        setNewMail
    ] = useState<string>("");

    const handleResetStates = () => {
        setPassword("");
        setNewMail("");
        onClose();
    };

    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleChangeMail = async () => {
        const trimmedMail = newMail.trim().toLowerCase();

        if (!trimmedMail || !password) {
            NCoreUIKitSnackBar.open({
                title: localize("fill-all-fields")
            });
            return;
        }

        if (!isValidEmail(trimmedMail)) {
            NCoreUIKitSnackBar.open({
                title: "Lütfen geçerli bir e-posta adresi giriniz."
            });
            return;
        }

        if (currentMail && trimmedMail === currentMail.trim().toLowerCase()) {
            NCoreUIKitSnackBar.open({
                title: "Yeni e-posta eskisinden farklı olmalıdır."
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const result = await RESTServiceContext.action("changeMail", {
                newMail: trimmedMail,
                password: password
            });

            if (result.isError) {
                let errorMsg = result.message || localize("something-went-wrong");
                if (result.message === "wrong-password") {
                    errorMsg = "Girdiğiniz mevcut şifre hatalı.";
                } else if (result.message === "mail-already-exists") {
                    errorMsg = "Bu e-posta adresi zaten kullanımda.";
                } else if (result.message === "new-mail-same-with-old-mail") {
                    errorMsg = "Yeni e-posta eskisinden farklı olmalıdır.";
                }
                NCoreUIKitSnackBar.open({
                    title: errorMsg
                });
            } else {
                await updateUser({
                    mail: trimmedMail
                });

                RESTServiceContext.emitWithKey(SERVICE_EVENT_TYPES.USER_UPDATED, {
                    mail: trimmedMail
                });

                NCoreUIKitSnackBar.open({
                    title: "E-posta adresiniz başarıyla güncellendi."
                });

                handleResetStates();
                onSuccess?.();
            }
        } catch (err) {
            console.error(err);
            NCoreUIKitSnackBar.open({
                title: localize("something-went-wrong")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <Fragment>
        <Modal
            onClosed={handleResetStates}
            portalName="modal-system"
            id="change-mail-modal"
            isActive={isVisible}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={stylesheet.headerContainer}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("change-mail")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <TextInput
                        placeholder={localize("new-mail")}
                        title={localize("new-mail")}
                        onChangeText={setNewMail}
                        spreadBehaviour="stretch"
                        value={newMail}
                    />
                    <TextInput
                        placeholder={localize("current-password")}
                        title={localize("current-password")}
                        onChangeText={setPassword}
                        spreadBehaviour="stretch"
                        value={password}
                        variant="hidden"
                    />
                </View>
                <View
                    style={[
                        stylesheet.footerContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        customBorderColor="secondaryButton"
                        customColor="secondaryButton"
                        onPress={handleResetStates}
                        title={localize("cancel")}
                        variant="filled"
                    />
                    <Button
                        onPress={handleChangeMail}
                        title={localize("save")}
                        isLoading={isSubmitting}
                    />
                </View>
            </View>
        </Modal>
    </Fragment>;
};

export default ChangeMailModal;
